import { useEffect, useState, useMemo } from 'react';
import mermaid from 'mermaid';

const MermaidDiagram = ({ chart, className = '', style = {} }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [svgContent, setSvgContent] = useState('');

  // Memoize the chart to prevent unnecessary re-renders
  const memoizedChart = useMemo(() => chart, [chart]);

  useEffect(() => {
    // Initialize Mermaid with dark theme configuration
    mermaid.initialize({
      startOnLoad: false,
      theme: 'dark',
      themeVariables: {
        primaryColor: '#3b82f6',
        primaryTextColor: '#e5e7eb',
        primaryBorderColor: '#1e40af',
        lineColor: '#6b7280',
        background: '#0f172a',
        tertiaryColor: '#1e293b',
        darkMode: true,
        fontFamily: 'Inter, system-ui, sans-serif'
      },
      securityLevel: 'loose',
      pie: {
        textPosition: 0.75
      },
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'basis'
      },
      gantt: {
        useMaxWidth: true
      }
    });

    const renderDiagram = async () => {
      // Test with simple chart if no chart provided
      const testChart = memoizedChart || `pie title "Test Chart"
    "A" : 40
    "B" : 60`;

      if (testChart) {
        try {
          setIsLoading(true);
          setError(null);
          setSvgContent('');
          
          // Generate unique ID for this diagram
          const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
          
          // Clean and validate the chart code
          let cleanChart = testChart.trim();
          
          // Render the diagram
          const { svg } = await mermaid.render(id, cleanChart);
          setSvgContent(svg);
          setIsLoading(false);
        } catch (error) {
          console.error('Mermaid render error:', error.message);
          setError(error.message);
          setIsLoading(false);
          setSvgContent(`
            <div style="padding: 20px; text-align: center; color: #ef4444; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 8px;">
              <p style="margin: 0; font-weight: 600;">Unable to render diagram</p>
              <p style="margin: 8px 0 0 0; font-size: 0.875rem; opacity: 0.8;">Please check the diagram syntax</p>
              <p style="margin: 8px 0 0 0; font-size: 0.75rem; opacity: 0.6;">${error.message}</p>
              <details style="margin-top: 8px; text-align: left;">
                <summary style="cursor: pointer; font-size: 0.75rem;">Show chart code</summary>
                <pre style="margin: 4px 0 0 0; font-size: 0.6rem; background: rgba(0,0,0,0.3); padding: 4px; border-radius: 4px; overflow: auto;">${testChart}</pre>
              </details>
            </div>
          `);
        }
      } else {
        setIsLoading(false);
        setSvgContent(`
          <div style="padding: 20px; text-align: center; color: #9ca3af;">
            <p style="margin: 0;">No chart data provided</p>
          </div>
        `);
      }
    };

    renderDiagram();
  }, [memoizedChart]);

  if (isLoading) {
    return (
      <div 
        className={`mermaid-diagram ${className}`}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          ...style
        }}
      >
        <div style={{ 
          padding: '20px', 
          color: '#9ca3af', 
          textAlign: 'center' 
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '2px solid #3b82f6',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 8px'
          }} />
          <p style={{ margin: '0', fontSize: '0.875rem' }}>Loading diagram...</p>
        </div>
      </div>
    );
  }

  if (svgContent) {
      return (
    <div 
      className={`mermaid-diagram ${className}`}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        maxWidth: '100%',
        overflow: 'auto',
        ...style
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
  }

  return (
    <div 
      className={`mermaid-diagram ${className}`}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        ...style
      }}
    />
  );
};

export default MermaidDiagram; 